import { useState, useEffect, useCallback } from 'react';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Employee,     
  DashboardStats,
  User,
  Department,
  Position,
  Loan
} from '@/types';


// Transform backend loan/advance data to frontend format
const transformLoanData = (backendLoan: any): Loan => {
  return {
    id: backendLoan.id?.toString() || '',
    employeeId: backendLoan.employee_id?.toString() || '',
    amount: parseFloat(backendLoan.amount) || 0,
    installmentAmount: parseFloat(backendLoan.installment_amount) || 0,
    purpose: backendLoan.purpose || '',
    notes: backendLoan.notes || '',
    requestDate: backendLoan.request_date || '',
    approvalDate: backendLoan.approval_date || '',
    status: backendLoan.status || 'pending',
    remainingAmount: parseFloat(backendLoan.remaining_amount) || parseFloat(backendLoan.amount) || 0,
    monthsForPayment: parseInt(backendLoan.months_for_payment) || 1,
    startMonth: backendLoan.start_month || ''
  };
};

// Transform backend employee data to frontend format
const transformEmployeeData = (backendEmployee: any): Employee => {
  return {
    id: backendEmployee.id?.toString() || '',
    employeeId: backendEmployee.employeeId || backendEmployee.employee_id || '',
    firstName: backendEmployee.firstName || backendEmployee.first_name || '',
    lastName: backendEmployee.lastName || backendEmployee.last_name || '',
    otherName: backendEmployee.otherName || backendEmployee.other_name,
    email: backendEmployee.email || '',
    phone: backendEmployee.phone || '',
    cellNumber: backendEmployee.cellNumber || backendEmployee.cell_number,
    jobTitle: backendEmployee.jobTitle || backendEmployee.job_title,
    division: backendEmployee.division,
    dateOfBirth: backendEmployee.dateOfBirth || backendEmployee.date_of_birth,
    gender: backendEmployee.gender || 'other',
    engagementDate: backendEmployee.engagementDate || backendEmployee.engagement_date,
    joinDate: backendEmployee.joinDate || backendEmployee.join_date,
    hireDate: backendEmployee.hireDate || backendEmployee.hire_date,
    status: backendEmployee.status || 'active',
    basicSalary: parseFloat(backendEmployee.basicSalary || backendEmployee.basic_salary) || 0,
    basicPay: parseFloat(backendEmployee.basicPay || backendEmployee.basic_pay) || 0,
    nrc: backendEmployee.nrc,
    manNumber: backendEmployee.manNumber || backendEmployee.man_number,
    socialSecurityNo: backendEmployee.socialSecurityNo || backendEmployee.social_security_no,
    bankName: backendEmployee.bankName || backendEmployee.bank_name,
    bankAccount: backendEmployee.bankAccount || backendEmployee.bank_account,
    bankBranch: backendEmployee.bankBranch || backendEmployee.bank_branch,
    unionType: backendEmployee.unionType || backendEmployee.union_type,
    contributionType: backendEmployee.contributionType || backendEmployee.contribution_type,
    salaryScale: backendEmployee.salaryScale || backendEmployee.salary_scale,
    advanceBF: parseFloat(backendEmployee.advanceBF || backendEmployee.advance_bf) || 0,
    leaveDaysPerMonth: parseFloat(backendEmployee.leaveDaysPerMonth || backendEmployee.leave_days_per_month) || 0,
    mlifeContributionPercentage: parseFloat(backendEmployee.mlifeContributionPercentage || backendEmployee.mlife_contribution_percentage) || 0,
    taxId: backendEmployee.taxId || backendEmployee.tax_id,
    nhimaNumber: backendEmployee.nhimaNumber || backendEmployee.nhima_number,
    userId: backendEmployee.userId || backendEmployee.user_id,
    department_id: backendEmployee.department_id,
    position_id: backendEmployee.position_id,
  };
};

// Custom hook for consistent data loading across the app
export function useAppData() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();

  // Load all data - removed toast from dependencies to prevent infinite loop
  const loadAllData = useCallback(async () => {
    // Wait until auth has finished loading and user with cid is available
    if (authLoading || !user || user.cid == null) {
      return;
    }

    setLoading(true);
    
    try {
      // Use Promise.allSettled instead of Promise.all to handle partial failures
      const cid = user.cid;
      const results = await Promise.allSettled([
        apiService.getEmployees(cid),
        apiService.getDepartments(cid),
        apiService.getPositions(cid),
        apiService.getDashboardStats(cid),
        apiService.getUsers(cid),
        apiService.getAdvances(cid)
      ]);
      
      // Process results, setting data for successful requests and handling errors for failed ones
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          let data = result.value;
          
          // Handle API response structure { message, status, data }
          if (result.value && typeof result.value === 'object' && 'data' in result.value) {
            data = result.value.data;
          }
          
          switch (index) {
            case 0: 
              const employeesData = Array.isArray(data) ? data.map(transformEmployeeData) : [];
              setEmployees(employeesData); 
              break;
            case 1: setDepartments(data as Department[]); break;
            case 2: setPositions(data as Position[]); break;
            case 3: setDashboardStats(data as DashboardStats); break;
            case 4: setUsers(data as User[]); break;
            case 5: 
              const loansData = Array.isArray(data) ? data.map(transformLoanData) : [];
              setLoans(loansData); 
              break;
          }
        } else {
          console.error(`Failed to load data for index ${index}:`, result.reason);
        }
      });
    } catch (error) {
      console.error('Error loading app data:', error);
    } finally {
      setLoading(false);
    }
  }, [authLoading, user?.cid]); // Re-run when council changes or auth finishes

  // Initial data load
  useEffect(() => {
    // Only load when auth is done and user/cid are ready
    if (!authLoading && user && user.cid != null) {
      loadAllData();
    }
  }, [authLoading, user?.cid, loadAllData]);

  // Refresh specific data after updates
  const refreshEmployees = useCallback(async () => {
    if (user?.cid == null) {
      return null;
    }
    try {
      const result = await apiService.getEmployees(user.cid);
      let data = result;
      if (result && typeof result === 'object' && 'data' in result) {
        data = (result as { data: unknown }).data;
      }
      const employeesData = Array.isArray(data) ? data.map(transformEmployeeData) : [];
      setEmployees(employeesData);
      return employeesData;
    } catch (error) {
      console.error('Error refreshing employees:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh employee data',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, user?.cid]);

  
  const refreshDepartments = useCallback(async () => {
    try {
      const data = await apiService.getDepartments(user?.cid ?? null);
      setDepartments(data);
      return data;
    } catch (error) {
      console.error('Error refreshing departments:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh department data',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, user?.cid]);
  
  const refreshPositions = useCallback(async () => {
    try {
      const data = await apiService.getPositions(user?.cid ?? null);
      setPositions(data);
      return data;
    } catch (error) {
      console.error('Error refreshing positions:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh position data',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, user?.cid]);
  

  const refreshUsers = useCallback(async () => {
    try {
      const response = await apiService.getUsers(user?.cid ?? null);
      let data = response;
      
      // Handle API response structure { message, status, data }
      if (response && typeof response === 'object' && 'data' in response) {
        data = response.data;
      }
      
      // Ensure data is an array
      const usersData = Array.isArray(data) ? data : [];
      setUsers(usersData);
      return usersData;
    } catch (error) {
      console.error('Error refreshing users:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh users data',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  const refreshDashboardStats = useCallback(async () => {
    if (user?.cid == null) {
      return null;
    }
    try {
      const result = await apiService.getDashboardStats(user.cid);
      let data = result;
      if (result && typeof result === 'object' && 'data' in result) {
        data = (result as { data: unknown }).data;
      }
      setDashboardStats(data as DashboardStats);
      return data as DashboardStats;
    } catch (error) {
      console.error('Error refreshing dashboard stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh dashboard statistics',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, user?.cid]);

  const refreshLoans = useCallback(async () => {
    if (user?.cid == null) {
      return null;
    }
    try {
      const response = await apiService.getAdvances(user.cid);
      let data = response;
      
      // Handle API response structure { message, status, data }
      if (response && typeof response === 'object' && 'data' in response) {
        data = response.data;
      }
      
      // Ensure data is an array and transform it
      const loansData = Array.isArray(data) ? data.map(transformLoanData) : [];
      setLoans(loansData);
      return loansData;
    } catch (error) {
      console.error('Error refreshing advances:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh advance data',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, user?.cid]);

  return {
    data: {
      employees,
      departments,
      positions,
      dashboardStats,
      users,
      loans
    },
    loading,
    refresh: {
      all: loadAllData,
      employees: refreshEmployees,
      departments: refreshDepartments,
      positions: refreshPositions,  
      users: refreshUsers,
      dashboardStats: refreshDashboardStats,
      loans: refreshLoans
    }
  };
} 
